import React, { useMemo, useState, useCallback } from "react";
import {
  Collapse, Input, Badge, Button, Checkbox, Space, Typography, Row, Col, Divider, Tooltip,
} from "antd";
import { ClearOutlined, LockOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

export type Permission = { action: string; subject: string };

const { Panel } = Collapse;

export interface PermissionSelectorProps {
  permissions: Permission[];
  value?: string[];                 
  onChange?: (keys: string[]) => void;
  loading?: boolean;
  disabled?: boolean;
  lockedKeys?: string[];           
  style?: React.CSSProperties;
  className?: string;
}

const CRUD = ["create", "read", "update", "delete"] as const;
const keyOf = (p: Permission) => `${p.action}::${p.subject}`;
const subjectOfKey = (k: string) => k.split("::")[1];
const normalize = (s: string) => s.toLowerCase();

const actionIcon = (action: string) => {
  switch (action) {
    case "create": return <i className="ti ti-plus"></i>;
    case "read": return <i className="ti ti-eye"></i>;
    case "update": return <i className="ti ti-edit"></i>;
    case "delete": return <i className="ti ti-trash"></i>;
    default: return <i className="ti ti-list"></i>;
  }
};

const ALWAYS_REQUIRED_PERMISSION_KEYS = ["read::Dashboard", "read::Payroll", "read::Leave"] as const;

const PermissionSelector: React.FC<PermissionSelectorProps> = ({
  permissions,
  value = [],
  onChange,
  loading,
  disabled,
  lockedKeys = [],
  style,
  className,
}) => {
  const [query, setQuery] = useState("");
  const { t } = useTranslation();

  const effectiveLockedKeys = useMemo(() => {
    const out = new Set<string>();

    (lockedKeys || []).forEach((k) => out.add(k));

    for (const item of ALWAYS_REQUIRED_PERMISSION_KEYS) {
      if (item.includes("::")) {
        const [a, s] = item.split("::");
        const aL = normalize(a);
        const sL = normalize(s);
        permissions.forEach((p) => {
          if (normalize(p.action) === aL && normalize(p.subject) === sL) {
            out.add(keyOf(p));
          }
        });
      } else {
        const sL = normalize(item);
        permissions.forEach((p) => {
          if (normalize(p.subject) === sL) out.add(keyOf(p));
        });
      }
    }

    return Array.from(out);
  }, [permissions, lockedKeys]);

  // ensure locked keys are always present in the working selection
  const safeValue = useMemo(
    () => Array.from(new Set([...(value || []), ...effectiveLockedKeys])),
    [value, effectiveLockedKeys]
  );

  // group by subject
  const grouped = useMemo(() => {
    const map: Record<string, Permission[]> = {};
    for (const p of permissions) (map[p.subject] ||= []).push(p);
    for (const s of Object.keys(map)) map[s].sort((a, b) => a.action.localeCompare(b.action));
    return Object.entries(map).sort(([a], [b]) =>
      t("roles.futures." + a).localeCompare(t("roles.futures." + b))
    );
  }, [permissions, t]);

  // filter by query (action or subject)
  const filteredBySubject = useMemo(() => {
    const q = normalize(query);
    if (!q) return grouped;
    return grouped
      .map(([subject, items]) => [
        subject,
        items.filter(
          (p) =>
            normalize(t("roles.futures." + p.subject)).includes(q) ||
            normalize(t("roles.actions." + p.action)).includes(q)
        ),
      ] as [string, Permission[]])
      .filter(([, items]) => items.length > 0);
  }, [grouped, query, t]);

  const setKeys = useCallback(
    (next: string[]) => {
      const union = Array.from(new Set([...(next || []), ...effectiveLockedKeys]));
      onChange?.(union);
    },
    [onChange, effectiveLockedKeys]
  );

  const toggleAllFiltered = useCallback(() => {
    const allFilteredKeys = filteredBySubject.flatMap(([, items]) => items.map(keyOf));
    setKeys(allFilteredKeys);
  }, [filteredBySubject, setKeys]);

  const clearAll = useCallback(() => setKeys([]), [setKeys]);

  const handleSubjectChange = (subject: string, nextKeys: any[]) => {
    const withoutSubject = safeValue.filter((k) => subjectOfKey(k) !== subject);
    setKeys([...withoutSubject, ...(nextKeys as string[])]);
  };

  const selectAllSubject = (subject: string, items: Permission[]) => {
    const subjectKeys = items.map(keyOf);
    const withoutSubject = safeValue.filter((k) => subjectOfKey(k) !== subject);
    setKeys([...withoutSubject, ...subjectKeys]);
  };

  const clearSubject = (subject: string) => {
    // remove only non-locked keys for this subject
    const next = safeValue.filter(
      (k) => subjectOfKey(k) !== subject || effectiveLockedKeys.includes(k)
    );
    setKeys(next);
  };

  const selectCRUD = (subject: string, items: Permission[]) => {
    const crudKeys = items
      .filter((p) => (CRUD as readonly string[]).includes(p.action as any))
      .map(keyOf);
    const withoutSubject = safeValue.filter((k) => subjectOfKey(k) !== subject);
    setKeys([...withoutSubject, ...crudKeys]);
  };

  const selectedCount = safeValue.length;
  const totalCount = permissions.length;

  return (
    <div className={className} style={style}>
      <Space direction="vertical" size={10} style={{ width: "100%" }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Input.Search
            allowClear
            placeholder={t("roles.form.search")}
            onChange={(e) => setQuery(e.target.value)}
            style={{ maxWidth: 360 }}
            disabled={disabled}
          />
          <Space wrap>
            <Tooltip title={t("roles.form.select_all_filtered")}>
              <Button onClick={toggleAllFiltered} disabled={disabled || loading}>
                {t("roles.form.select_all_filtered")}
              </Button>
            </Tooltip>
            <Tooltip title={t("roles.form.clear_all")}>
              <Button onClick={clearAll} icon={<ClearOutlined />} disabled={disabled || loading}>
                {t("roles.form.clear_all")}
              </Button>
            </Tooltip>
            {/* <Badge count={`${selectedCount}/${totalCount}`} style={{ backgroundColor: "#6C3DF4" }} /> */}
          </Space>
        </Space>

        <Divider style={{ margin: "8px 0 12px" }} />

        <Collapse accordion bordered={false}>
          {filteredBySubject.map(([subject, items]) => {
            const visibleItems = items.filter((p) => !effectiveLockedKeys.includes(keyOf(p)));
            if (visibleItems.length === 0) return null; 

            const subjectSelected = safeValue.filter((k) => subjectOfKey(k) === subject && visibleItems.some((p) => keyOf(p) === k));
            const subjectTotal = visibleItems.length;

            return (
              <Panel
                key={subject}
                header={
                  <Space style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                    <Space>
                      <Typography.Text strong>{t("roles.futures." + subject)}</Typography.Text>
                      <Badge
                        count={`${subjectSelected.length}/${subjectTotal}`}
                        style={{ backgroundColor: subjectSelected.length ? "#6C3DF4" : undefined }}
                      />
                    </Space>
                    <Space>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectAllSubject(subject, visibleItems);
                        }}
                        disabled={disabled}
                      >
                        {t("roles.form.select_all")}
                      </Button>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearSubject(subject);
                        }}
                        disabled={disabled}
                      >
                        {t("roles.form.clear")}
                      </Button>
                    </Space>
                  </Space>
                }
              >
                <Checkbox.Group
                  style={{ width: "100%" }}
                  value={subjectSelected}
                  onChange={(next) => handleSubjectChange(subject, next)}
                  disabled={disabled || loading}
                >
                  <Row gutter={[8, 8]}>
                    {visibleItems.map((p) => {
                      const k = keyOf(p);
                      return (
                        <Col xs={24} sm={12} md={8} lg={6} key={k}>
                          <Checkbox value={k}>
                            <Space>
                              {actionIcon(p.action)}
                              <span style={{ textTransform: "capitalize" }}>
                                {t("roles.actions." + p.action)}
                              </span>
                            </Space>
                          </Checkbox>
                        </Col>
                      );
                    })}
                  </Row>
                </Checkbox.Group>
              </Panel>
            );
          })}
        </Collapse>
      </Space>
    </div>
  );
};

export default PermissionSelector;
